import React, { useEffect, useState } from "react";
import { api, apiJson } from "@/api/client.js";
import { MESSAGE_CHANNELS } from "@/config/orignalAppConfig.js";
import { useOrignalUi } from "@/hooks/useOrignalUi.js";
import { clearDraft, loadDraft, saveDraft } from "@/lib/draftStore.js";
import { localizeDemo } from "@/i18n/orignalI18n.js";
import { useAuth } from "@/context/AuthContext.jsx";

export function MessagesPage() {
  const { ui, lang } = useOrignalUi();
  const { user } = useAuth();
  const [channel, setChannel] = useState("general");
  const [items, setItems] = useState([]);
  const [members, setMembers] = useState([]);
  const [body, setBody] = useState(() => loadDraft("message", ""));

  async function reload(ch = channel) {
    const [msg, mem] = await Promise.all([api(`/api/messages?channel=${ch}`), api("/api/members")]);
    setItems(msg.items);
    setMembers(mem.items);
  }

  useEffect(() => {
    reload().catch(() => {});
  }, [channel]);

  useEffect(() => saveDraft("message", body), [body]);

  const names = Object.fromEntries(members.map((m) => [m.id, m.display_name]));
  const imported = items.filter((m) => m.source === "Messenger");

  async function send(e) {
    e.preventDefault();
    if (!body.trim()) return;
    await apiJson("/api/messages", { channel, body });
    clearDraft("message");
    setBody("");
    await reload();
  }

  return (
    <main className="og-page" data-testid="page-messages">
      <h1>{ui.messages.title}</h1>
      <p className="og-lead">{ui.messages.subtitle}</p>
      <div className="og-chips" data-testid="message-channels">
        {MESSAGE_CHANNELS.map((ch) => (
          <button key={ch} type="button" className={`og-chip${channel === ch ? " is-active" : ""}`} onClick={() => setChannel(ch)}>
            {ui.channels[ch]}
          </button>
        ))}
      </div>
      <div className="og-card og-messages">
        {items.length === 0 ? <p data-testid="messages-empty">{ui.messages.empty}</p> : null}
        {items.map((m) => {
          const mine = m.author_id && user?.member_id && m.author_id === user.member_id;
          return (
            <article key={m.id} className={`og-msg${mine ? " og-msg--me" : ""}`}>
              <strong>{names[m.author_id] || m.author_id}</strong>
              <p>{localizeDemo(m.body, lang)}</p>
              {m.source === "Messenger" ? <small>{ui.messages.source}: Messenger · {ui.demo.imported}</small> : null}
              <details className="og-msg__more">
                <summary>…</summary>
                <button type="button" className="og-btn" onClick={() => apiJson("/api/messages/" + m.id + "/convert", { to: "task", trip_id: "trip_terrain_3j" })}>{ui.nav.tasks}</button>
                <button type="button" className="og-btn" onClick={() => apiJson("/api/messages/" + m.id + "/convert", { to: "expense", trip_id: "trip_terrain_3j" })}>{ui.nav.expenses}</button>
                <button type="button" className="og-btn" onClick={() => apiJson("/api/messages/" + m.id + "/convert", { to: "note", trip_id: "trip_terrain_3j" })}>{ui.observations.title}</button>
              </details>
            </article>
          );
        })}
      </div>
      <form className="og-inline" onSubmit={send}>
        <input className="og-input" value={body} onChange={(e) => setBody(e.target.value)} placeholder={ui.messages.write} data-testid="message-body" />
        <button className="og-btn og-btn--primary" type="submit" data-testid="message-send">{ui.messages.send}</button>
      </form>
      <section className="og-card" data-testid="imported-sources">
        <h3>{ui.messages.importedSources}</h3>
        <p>{ui.messages.importedNote}</p>
        <p>{imported.length} · Messenger</p>
      </section>
    </main>
  );
}
